/**
 * Hook for reactive network status detection with quality assessment.
 *
 * Combines three signals:
 * 1. `navigator.onLine` — browser-level connectivity (offline/online events)
 * 2. Network Information API (`navigator.connection`) — effective type, downlink, rtt
 * 3. Periodic lightweight ping to the backend API — real reachability check
 *
 * Exposes a `networkQuality` level: 'offline' | 'poor' | 'fair' | 'good'.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { getApiBaseUrl } from '../config/env';

// ─── Types ───────────────────────────────────────────────────────────────────

export type NetworkQuality = 'offline' | 'poor' | 'fair' | 'good';

export interface NetworkStatus {
  /** Whether the browser currently reports connectivity */
  isOnline: boolean;
  /** Whether the app was offline at any point during this session */
  wasOffline: boolean;
  /** Timestamp of the last time the app was online */
  lastOnlineAt: Date | null;
  /** Assessed quality level */
  networkQuality: NetworkQuality;
  /** Effective connection type from the Network Information API (e.g. '4g', '3g', '2g', 'slow-2g') */
  effectiveType: string | null;
  /** Downlink speed in Mbps (Network Information API) */
  downlinkMbps: number | null;
  /** Round-trip time in ms (Network Information API) */
  rttMs: number | null;
  /** Last measured ping to the backend in ms (null if not yet measured) */
  pingMs: number | null;
  /** Whether the backend API is actually reachable */
  isApiReachable: boolean;
}

// ─── Network Information API typings (not in standard TS lib yet) ─────────

interface NetworkInformation extends EventTarget {
  readonly effectiveType?: string;
  readonly downlink?: number;
  readonly rtt?: number;
  readonly saveData?: boolean;
  onchange?: EventListener;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

function getConnection(): NetworkInformation | null {
  return navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection ?? null;
}

// ─── Quality heuristics ──────────────────────────────────────────────────────

/** Ping interval while online (30 s) and while suspected poor (8 s) */
const PING_INTERVAL_GOOD = 30_000;
const PING_INTERVAL_POOR = 8_000;

/** Thresholds */
const PING_POOR_MS = 3000;    // ping > 3s → poor
const PING_FAIR_MS = 1000;    // ping > 1s → fair
const PING_TIMEOUT_MS = 5000; // abort after 5s

function assessQuality(
  browserOnline: boolean,
  effectiveType: string | null,
  rtt: number | null,
  pingMs: number | null,
  apiReachable: boolean,
): NetworkQuality {
  // Completely offline
  if (!browserOnline) return 'offline';

  // API unreachable despite browser online → treat as offline
  if (!apiReachable && pingMs !== null) return 'offline';

  // Network Information API hints
  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'poor';

  // Ping-based assessment (most reliable)
  if (pingMs !== null) {
    if (pingMs >= PING_POOR_MS) return 'poor';
    if (pingMs >= PING_FAIR_MS) return 'fair';
    return 'good';
  }

  // Network Information API rtt
  if (rtt !== null) {
    if (rtt >= 2000) return 'poor';
    if (rtt >= 800) return 'fair';
    return 'good';
  }

  if (effectiveType === '3g') return 'fair';

  // Default: online but no further info
  return 'good';
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [wasOffline, setWasOffline] = useState(() => !navigator.onLine);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(() =>
    navigator.onLine ? new Date() : null,
  );

  // Network Information API values
  const [effectiveType, setEffectiveType] = useState<string | null>(
    () => getConnection()?.effectiveType ?? null,
  );
  const [downlinkMbps, setDownlinkMbps] = useState<number | null>(
    () => getConnection()?.downlink ?? null,
  );
  const [rttMs, setRttMs] = useState<number | null>(
    () => getConnection()?.rtt ?? null,
  );

  // Ping state
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [isApiReachable, setIsApiReachable] = useState(true);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Browser online/offline events ────────────────────────────────────────

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setLastOnlineAt(new Date());
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    setIsApiReachable(false);
    setPingMs(null);
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // ── Network Information API ──────────────────────────────────────────────

  useEffect(() => {
    const conn = getConnection();
    if (!conn) return;

    const update = () => {
      setEffectiveType(conn.effectiveType ?? null);
      setDownlinkMbps(conn.downlink ?? null);
      setRttMs(conn.rtt ?? null);
    };

    conn.addEventListener('change', update);
    return () => conn.removeEventListener('change', update);
  }, []);

  // ── Periodic backend ping ────────────────────────────────────────────────

  const doPing = useCallback(async () => {
    if (!navigator.onLine) return;

    const start = performance.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    try {
      await fetch(`${getApiBaseUrl()}/health`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      const elapsed = Math.round(performance.now() - start);
      setPingMs(elapsed);
      setIsApiReachable(true);
    } catch {
      // Timeout or network error
      setPingMs(PING_TIMEOUT_MS);
      setIsApiReachable(false);
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    // Initial ping
    void doPing();

    // Adaptive interval based on current quality
    const scheduleNext = () => {
      const interval = isApiReachable && (pingMs === null || pingMs < PING_FAIR_MS)
        ? PING_INTERVAL_GOOD
        : PING_INTERVAL_POOR;

      pingIntervalRef.current = setInterval(() => void doPing(), interval);
    };

    scheduleNext();

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, [doPing, isApiReachable, pingMs]);

  // ── Compute quality ──────────────────────────────────────────────────────

  const networkQuality = assessQuality(
    isOnline,
    effectiveType,
    rttMs,
    pingMs,
    isApiReachable,
  );

  return {
    isOnline,
    wasOffline,
    lastOnlineAt,
    networkQuality,
    effectiveType,
    downlinkMbps,
    rttMs,
    pingMs,
    isApiReachable,
  };
}
